import { Head, Link } from "@inertiajs/react"
import { Building2 } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty"
import AppLayout from "@/layouts/app-layout"
import { agentPropertiesPath } from "@/routes"
import type { BreadcrumbItem, Property } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [{ title: "My Properties", href: agentPropertiesPath() }]

interface Props {
  properties: (Property & { suites_count?: number; available_count?: number })[]
}

export default function Index({ properties }: Props) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="My Properties" />

      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">My Properties</h1>
          <p className="text-muted-foreground text-sm">Properties you're assigned to as a leasing agent.</p>
        </div>

        {properties.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Building2 />
              </EmptyMedia>
              <EmptyTitle>No properties assigned</EmptyTitle>
              <EmptyDescription>You haven't been assigned to any properties yet.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {properties.map((property) => (
              <Card key={property.id} className="transition-shadow hover:shadow-md">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base">{property.name}</CardTitle>
                    <Badge variant={(property.available_count ?? 0) > 0 ? "default" : "secondary"}>
                      {property.available_count ?? 0}/{property.suites_count ?? 0} available
                    </Badge>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    {property.address}, {property.city}, {property.state} {property.zip_code}
                  </p>
                </CardHeader>
                <CardContent>
                  <Button size="sm" variant="outline" render={<Link href={`/agent/properties/${property.id}`} />}>
                    Manage
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
